import React, { useState, useEffect } from 'react';
import { Globe, Sun, Moon, Sunrise, Sunset } from 'lucide-react';
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from '../utils/localStorage';

export default function WorldClock() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedCities, setSelectedCities] = useState([
    'new-york', 'london', 'tokyo', 'paris', 'dubai', 'sydney',
    'beijing', 'hong-kong', 'singapore', 'seoul', 'bangkok', 'mumbai', 'shanghai', 'jakarta',
    'berlin', 'moscow', 'rome', 'madrid', 'amsterdam', 'istanbul',
    'los-angeles', 'chicago', 'toronto', 'vancouver', 'mexico-city',
    'melbourne', 'auckland',
    'sao-paulo', 'buenos-aires', 'lima',
    'cairo', 'johannesburg', 'lagos'
  ]);

  // World cities database
  const worldCities = [
    // Asia
    { id: 'tokyo', name: 'Tokyo', country: 'Japan', timezone: 'Asia/Tokyo', emoji: '🇯🇵' },
    { id: 'beijing', name: 'Beijing', country: 'China', timezone: 'Asia/Shanghai', emoji: '🇨🇳' },
    { id: 'hong-kong', name: 'Hong Kong', country: 'Hong Kong', timezone: 'Asia/Hong_Kong', emoji: '🇭🇰' },
    { id: 'singapore', name: 'Singapore', country: 'Singapore', timezone: 'Asia/Singapore', emoji: '🇸🇬' },
    { id: 'seoul', name: 'Seoul', country: 'South Korea', timezone: 'Asia/Seoul', emoji: '🇰🇷' },
    { id: 'bangkok', name: 'Bangkok', country: 'Thailand', timezone: 'Asia/Bangkok', emoji: '🇹🇭' },
    { id: 'mumbai', name: 'Mumbai', country: 'India', timezone: 'Asia/Kolkata', emoji: '🇮🇳' },
    { id: 'dubai', name: 'Dubai', country: 'UAE', timezone: 'Asia/Dubai', emoji: '🇦🇪' },
    { id: 'shanghai', name: 'Shanghai', country: 'China', timezone: 'Asia/Shanghai', emoji: '🇨🇳' },
    { id: 'jakarta', name: 'Jakarta', country: 'Indonesia', timezone: 'Asia/Jakarta', emoji: '🇮🇩' },
    
    // Europe
    { id: 'london', name: 'London', country: 'UK', timezone: 'Europe/London', emoji: '🇬🇧' },
    { id: 'paris', name: 'Paris', country: 'France', timezone: 'Europe/Paris', emoji: '🇫🇷' },
    { id: 'berlin', name: 'Berlin', country: 'Germany', timezone: 'Europe/Berlin', emoji: '🇩🇪' },
    { id: 'moscow', name: 'Moscow', country: 'Russia', timezone: 'Europe/Moscow', emoji: '🇷🇺' },
    { id: 'rome', name: 'Rome', country: 'Italy', timezone: 'Europe/Rome', emoji: '🇮🇹' },
    { id: 'madrid', name: 'Madrid', country: 'Spain', timezone: 'Europe/Madrid', emoji: '🇪🇸' },
    { id: 'amsterdam', name: 'Amsterdam', country: 'Netherlands', timezone: 'Europe/Amsterdam', emoji: '🇳🇱' },
    { id: 'istanbul', name: 'Istanbul', country: 'Turkey', timezone: 'Europe/Istanbul', emoji: '🇹🇷' },
    
    // North America
    { id: 'new-york', name: 'New York', country: 'USA', timezone: 'America/New_York', emoji: '🇺🇸' },
    { id: 'los-angeles', name: 'Los Angeles', country: 'USA', timezone: 'America/Los_Angeles', emoji: '🇺🇸' },
    { id: 'chicago', name: 'Chicago', country: 'USA', timezone: 'America/Chicago', emoji: '🇺🇸' },
    { id: 'toronto', name: 'Toronto', country: 'Canada', timezone: 'America/Toronto', emoji: '🇨🇦' },
    { id: 'vancouver', name: 'Vancouver', country: 'Canada', timezone: 'America/Vancouver', emoji: '🇨🇦' },
    { id: 'mexico-city', name: 'Mexico City', country: 'Mexico', timezone: 'America/Mexico_City', emoji: '🇲🇽' },
    
    // Oceania
    { id: 'sydney', name: 'Sydney', country: 'Australia', timezone: 'Australia/Sydney', emoji: '🇦🇺' },
    { id: 'melbourne', name: 'Melbourne', country: 'Australia', timezone: 'Australia/Melbourne', emoji: '🇦🇺' },
    { id: 'auckland', name: 'Auckland', country: 'New Zealand', timezone: 'Pacific/Auckland', emoji: '🇳🇿' },
    
    // South America
    { id: 'sao-paulo', name: 'São Paulo', country: 'Brazil', timezone: 'America/Sao_Paulo', emoji: '🇧🇷' },
    { id: 'buenos-aires', name: 'Buenos Aires', country: 'Argentina', timezone: 'America/Argentina/Buenos_Aires', emoji: '🇦🇷' },
    { id: 'lima', name: 'Lima', country: 'Peru', timezone: 'America/Lima', emoji: '🇵🇪' },
    
    // Africa
    { id: 'cairo', name: 'Cairo', country: 'Egypt', timezone: 'Africa/Cairo', emoji: '🇪🇬' },
    { id: 'johannesburg', name: 'Johannesburg', country: 'South Africa', timezone: 'Africa/Johannesburg', emoji: '🇿🇦' },
    { id: 'lagos', name: 'Lagos', country: 'Nigeria', timezone: 'Africa/Lagos', emoji: '🇳🇬' },
  ];

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Load selected cities from localStorage
  useEffect(() => {
    const saved = loadFromStorage(STORAGE_KEYS.WORLD_CLOCK_CITIES);
    if (saved) {
      setSelectedCities(saved);
    }
  }, []);

  // Save selected cities to localStorage// 保存选中的城市到本地存储
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.WORLD_CLOCK_CITIES, selectedCities);
  }, [selectedCities]);

  const getTimeInTimezone = (timezone) => {
    return new Date(currentTime.toLocaleString('en-US', { timeZone: timezone }));
  };

  const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return { hours, minutes, seconds };
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTimeDifference = (timezone) => {
    const local = new Date();
    const remote = getTimeInTimezone(timezone);
    const diffMs = remote - local;
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    
    if (diffHours === 0) return 'Same time';
    const sign = diffHours > 0 ? '+' : '';
    return `${sign}${diffHours}h`;
  };

  const getTimeOfDayIcon = (timezone) => {
    const time = getTimeInTimezone(timezone);
    const hour = time.getHours();
    
    if (hour >= 6 && hour < 12) return <Sunrise className="w-5 h-5 text-orange-300" />;
    if (hour >= 12 && hour < 18) return <Sun className="w-5 h-5 text-yellow-300" />;
    if (hour >= 18 && hour < 21) return <Sunset className="w-5 h-5 text-orange-400" />;
    return <Moon className="w-5 h-5 text-blue-200" />;
  };

  const localTime = formatTime(currentTime);
  const localDate = formatDate(currentTime);

  // Detect user's location (simplified - using browser timezone)
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userLocation = userTimezone.split('/').pop().replace(/_/g, ' ');

  return (
    <div className="space-y-6">
      {/* Hero Clock - Local Time */}
      <div className="bg-cyan-600/40 backdrop-blur-md rounded-3xl p-8 border border-cyan-400/30 shadow-2xl">
        <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 mb-6">
          <TimeDigit value={localTime.hours} />
          <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white">:</div>
          <TimeDigit value={localTime.minutes} />
          <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white">:</div>
          <TimeDigit value={localTime.seconds} />
        </div>

        <div className="text-center">
          <div className="text-white text-xl mb-2">
            Today: {localDate}
          </div>
          <div className="text-cyan-200 text-lg flex items-center justify-center gap-2">
            <Globe className="w-5 h-5" />
            {userLocation} (Your Location)
          </div>
        </div>
      </div>

      {/* World Clocks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {selectedCities.map(cityId => {
          const city = worldCities.find(c => c.id === cityId);
          if (!city) return null;
          
          const cityTime = getTimeInTimezone(city.timezone);
          const time = formatTime(cityTime);
          const timeDiff = getTimeDifference(city.timezone);
          const timeIcon = getTimeOfDayIcon(city.timezone);
          const date = cityTime.toLocaleDateString('en-US', { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric' 
          });

          return (
            <WorldClockCard
              key={city.id}
              city={city}
              time={time}
              date={date}
              timeDiff={timeDiff}
              timeIcon={timeIcon}
            />
          );
        })}
      </div>

      {selectedCities.length === 0 && (
        <div className="bg-blue-800/40 backdrop-blur-md rounded-2xl p-12 border border-blue-600/30 text-center">
          <Globe className="w-16 h-16 text-white/50 mx-auto mb-4" />
          <p className="text-white text-xl mb-2">No cities added yet</p>
          <p className="text-white/70">Search and add cities to see their time zones</p>
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-blue-800/40 backdrop-blur-md rounded-2xl p-6 border border-blue-600/30">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          World Clock Tips
        </h4>
        <ul className="text-blue-100 text-sm space-y-2">
          <li>• Time differences are calculated relative to your local time</li>
          <li>• Icons show time of day: 🌅 Morning, ☀️ Afternoon, 🌆 Evening, 🌙 Night</li>
          <li>• Your selected cities are saved automatically</li>
          <li>• Perfect for scheduling international meetings and calls</li>
        </ul>
      </div>
    </div>
  );
}

// Large Time Digit Component (Hero Clock)
function TimeDigit({ value }) {
  return (
    <div className="bg-gradient-to-b from-cyan-500/80 to-cyan-600/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-xl border-2 border-cyan-400/50">
      <div className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-white font-mono tracking-wider">
        {value}
      </div>
    </div>
  );
}

// World Clock Card Component
function WorldClockCard({ city, time, date, timeDiff, timeIcon }) {
  return (
    <div className="bg-cyan-600/40 backdrop-blur-md rounded-2xl p-6 border border-cyan-400/30 shadow-xl hover:shadow-2xl transition-all relative group">

      {/* City Info */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-3xl">{city.emoji}</span>
          <div>
            <h3 className="text-xl font-bold text-white">{city.name}</h3>
            <p className="text-cyan-200 text-sm">{city.country}</p>
          </div>
        </div>
      </div>

      {/* Time Display */}
      <div className="flex justify-center items-center gap-1 mb-4">
        <SmallTimeDigit value={time.hours} />
        <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">:</div>
        <SmallTimeDigit value={time.minutes} />
        <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">:</div>
        <SmallTimeDigit value={time.seconds} />
      </div>

      {/* Date */}
      <div className="text-center text-white/80 text-sm mb-3">
        {date}
      </div>

      {/* Time Difference and Icon */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-lg">
          {timeIcon}
          <span className="text-white font-semibold text-sm">{timeDiff}</span>
        </div>
      </div>
    </div>
  );
}

// Small Time Digit for World Clock Cards
function SmallTimeDigit({ value }) {
  return (
    <div className="bg-gradient-to-b from-cyan-500/80 to-cyan-600/80 rounded-lg px-2 py-1 sm:px-2 sm:py-1 shadow-lg border border-cyan-400/50">
      <div className="text-lg sm:text-xl md:text-2xl font-bold text-white font-mono">
        {value}
      </div>
    </div>
  );
}